import 'styles/components/settings/Settings.styl'

import { settingsAppConfig } from 'app/modules/user/selectors'
import { Component } from 'react'
import { connect } from 'react-redux'
import ACCESS from 'utils/Access'

import * as OrgSelectors from '../../app/modules/org/selectors'
import * as PropertiesSelectors from '../../app/modules/user/properties/selectors'
import Subnav from '../shared/Subnav'
import { PAGE_TITLES, HELP_CENTER_HELPDOC_URL } from './constants'
import SettingsPageHeader from './SettingsPageHeader'

class Settings extends Component {
  constructor(props) {
    super(props)
    this.state = {
      pageTitle: this.getSettingsPageTitle(),
    }
  }

  componentDidUpdate = prevProps => {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      this.setState({
        pageTitle: this.getSettingsPageTitle()
      })
    }
  }

  getSettingsPageTitle = () => {
    const { location: { pathname }} = this.props
    const pathObj = PAGE_TITLES.find((element => element.path === pathname))
    if (pathObj) { return pathObj.title }
    return false
  }

  createSubnav = () => {
    const { hasWorkspaces, settingsAppConfig } = this.props
    const { hasDesktopApp = true } = this.props

    const {
      hasAccount,
      hasAdminSettings,
      hasProfile,
      hasDriftAssistant,
      hasNotifications,
      hasEmail,
      hasMeetingsInSubnav,
      hasBilling,
      hasManageTeammates,
      hasTeams,
      hasOrgMeetings,
      hasDataPrivacy,
      hasSecurity,
      hasWidget,
      hasConvoRouting,
      hasBot,
      hasConversations,
      hasCompany,
      hasIntegrations,
      hasHelpDocs,
    } = settingsAppConfig

    const isPremium = ACCESS.hasGates('premium_drift')

    const mySettingsNavigationItems = [
      {
        label: 'MY SETTINGS',
        expanded: true,
        nodes: [
          {
            label: 'Account',
            path: '/settings2/account',
            external: true,
            hide: !hasAccount,
          },
          {
            label: 'Notifications',
            path: '/settings2/notifications',
            external: true,
            hide: !hasNotifications || hasDesktopApp,
            exact: false,
          },
          {
            label: 'Calendar',
            path: '/meetings/mine',
            external: true,
            hide: !hasMeetingsInSubnav,
          },
          {
            label: 'Drift Profile',
            path: '/settings2/profile',
            external: true,
            hide: !hasProfile,
          },
          {
            label: 'Email',
            path: '/settings2/email',
            external: true,
            hide: !hasEmail,
          },
          {
            label: 'Drift Assistant',
            path: '/settings2/assistant',
            external: true,
            hide: !hasDriftAssistant,
          },
        ],
      },
    ]

    const orgSettingsNavigationItems = [
      {
        label: 'ORGANIZATION SETTINGS',
        expanded: true,
        nodes: [
          {
            label: 'Plan and billing',
            path: '/settings2/billing',
            external: true,
            hide: !hasBilling,
          },
          {
            label: 'Manage teammates',
            path: '/settings2/team',
            external: true,
            hide: !hasManageTeammates,
          },
          {
            label: 'Teams',
            path: '/settings2/teams',
            external: true,
            hide: !hasTeams,
          },
          {
            label: 'Meetings',
            path: '/meetings/organization',
            external: true,
            hide: !hasOrgMeetings,
          },
          {
            label: 'Company details',
            path: '/settings2/company',
            external: true,
            hide: !hasCompany,
          },
          {
            label: 'Data privacy',
            path: '/settings2/consent',
            external: true,
            hide: !hasDataPrivacy,
          },
          {
            label: 'Security',
            path: '/settings2/security',
            external: true,
            hide: !hasSecurity,
          },
          {
            label: 'Workspaces',
            path: '/settings2/workspaces',
            external: true,
            exact: false,
            hide: !hasWorkspaces,
          },
        ],
      },
    ]

    const appSettingsNavigationItems = [
      {
        label: 'APP SETTINGS',
        expanded: true,
        nodes: [
          {
            label: 'Drift widget',
            path: '/settings2/widget',
            external: true,
            hide: !hasWidget,
          },
          {
            label: 'Conversations',
            path: '/settings2/conversations',
            external: true,
            hide: !hasConversations,
          },
          {
            label: 'Conversation routing',
            path: '/settings2/rules',
            external: true,
            hide: !hasConvoRouting,
          },
          {
            label: 'Bot',
            path: '/settings2/bot',
            external: true,
            icon: !isPremium ? 'lock' : null,
            disabled: !isPremium,
            hide: !hasBot,
          },
        ],
      },
    ]

    const adminNavigationItems = [
      ...orgSettingsNavigationItems,
      ...appSettingsNavigationItems,
      {
        label: 'Integrations',
        path: '/settings2/integrations',
        external: true,
        hide: !hasIntegrations
      },
      {
        label: 'Help',
        path: '/settings2/help',
        external: true,
      },
    ]

    const subnavItems = [
      ...mySettingsNavigationItems,
      ...(hasAdminSettings
        ? adminNavigationItems
        : [
          {
            label: 'Help Docs',
            path: HELP_CENTER_HELPDOC_URL,
            target: '_blank',
            rel: 'noopener noreferrer',
          },
          {
            label: 'Integrations',
            path: '/settings2/integrations',
            external: true,
          },
        ]),
    ]

    const hasMySettings = mySettingsNavigationItems[0].nodes.some((node) => !node.hide)
    const hasOrgSettings = orgSettingsNavigationItems[0].nodes.some((node) => !node.hide)
    const hasAppSettings = appSettingsNavigationItems[0].nodes.some((node) => !node.hide)

    const rbacUserSubnavItems = [
      ...(hasMySettings ? mySettingsNavigationItems : []),
      ...(hasOrgSettings ? orgSettingsNavigationItems : []),
      ...(hasAppSettings ? appSettingsNavigationItems : []),
      {
        label: 'Integrations',
        path: '/settings2/integrations',
        exact: false,
        hide: !hasIntegrations,
      },
      {
        label: 'Help Docs',
        path: HELP_CENTER_HELPDOC_URL,
        target: '_blank',
        rel: 'noopener noreferrer',
        hide: !hasHelpDocs,
      },
    ]

    return <Subnav
      title="Settings"
      open={true}
      nodes={ACCESS.isRbacUser() ? rbacUserSubnavItems : subnavItems}
    />

  }

  render() {
    const { pageTitle } = this.state

    return (
      <div className="settings-section">
        <div className="settings-rest can-hide-nav">
          {this.createSubnav()}
          <div className="settings-page">
            {pageTitle &&
              <SettingsPageHeader title={pageTitle} />
            }
            <div className="settings-page-content">{this.props.children}</div>
          </div>
        </div>
      </div>
    )
  }
}

const mapStateToProps = state => ({
  settingsAppConfig: settingsAppConfig(state),
  orgId: OrgSelectors.orgId(state),
  hasWorkspaces: state.workspaces && state.workspaces.hasWorkspaces,
  hasDesktopApp: PropertiesSelectors.isDesktopUser(state),
})

export default connect(mapStateToProps)(Settings)



// WEBPACK FOOTER //
// ./src/scripts/components/settings/Settings.js